import React, { useEffect, useState } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Plus, X } from 'react-feather';
import { usePermissions } from '../contexts/PermissionsContext';
import { useCalendar } from '../contexts/CalendarContext';
import EventModal from './EventModal';
import Modal from './Modal';
import { useResponsive } from '../hooks/useResponsive';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS }
});

interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  color: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

interface BigCalendarEvent extends CalendarEvent {
  start: Date;
  end: Date;
  resource: CalendarEvent;
}

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CalendarModal: React.FC<CalendarModalProps> = ({ isOpen, onClose }) => {
  const { can } = usePermissions();
  const { events, loading, fetchEvents } = useCalendar();
  const { isMobile } = useResponsive();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const canEdit = can('calendar:edit') || can('*:*');

  useEffect(() => {
    if (isOpen) {
      fetchEvents();
    }
  }, [isOpen, fetchEvents]);

  const calendarEvents: BigCalendarEvent[] = events.map(e => ({
    ...e,
    start: new Date(e.start_date.split('T')[0] + 'T00:00:00'),
    end: new Date(e.end_date.split('T')[0] + 'T00:00:00'),
    title: e.title,
    resource: e
  }));

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile calendar styles */}
      {isMobile && (
        <style>{`
          .rbc-toolbar {
            flex-wrap: wrap !important;
            gap: 8px !important;
            padding: 8px 0 !important;
          }
          .rbc-toolbar-label {
            width: 100% !important;
            text-align: center !important;
            order: -1 !important;
            font-size: 16px !important;
            font-weight: 600 !important;
            margin-bottom: 8px !important;
          }
          .rbc-btn-group {
            flex: 1 !important;
          }
          .rbc-btn-group button {
            font-size: 12px !important;
            padding: 6px 10px !important;
          }
          .rbc-header {
            font-size: 11px !important;
            padding: 4px !important;
          }
          .rbc-date-cell {
            font-size: 12px !important;
            padding: 2px !important;
          }
          .rbc-event {
            font-size: 10px !important;
            padding: 1px 4px !important;
          }
          .rbc-agenda-view table {
            font-size: 13px !important;
          }
          .rbc-agenda-date-cell, .rbc-agenda-time-cell {
            font-size: 12px !important;
            padding: 8px !important;
          }
          .rbc-agenda-event-cell {
            font-size: 13px !important;
            padding: 8px !important;
          }
        `}</style>
      )}
      {/* Shared Modal wrapper (audit #223): dialog semantics, Escape, focus trap, scroll lock */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        labelledById="calendar-modal-title"
        zIndex={9998}
        // While the nested EventModal is open, ignore backdrop/Escape here so a single
        // Escape closes only the child, not both stacked dialogs (audit #223).
        disableClose={eventModalOpen}
        contentStyle={{
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '1200px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
          {/* Header */}
          <div style={{
            padding: isMobile ? '16px' : '24px 32px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? '12px' : '0'
          }}>
            <div style={{ flex: isMobile ? '1 1 100%' : 'auto' }}>
              <h2 id="calendar-modal-title" style={{
                margin: 0,
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '700',
                color: '#151133'
              }}>
                Recruiting Calendar
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: isMobile ? '12px' : '14px',
                color: '#6c757d'
              }}>
                View upcoming recruiting events and deadlines
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: isMobile ? '1 1 100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              {canEdit && (
                <button
                  onClick={() => {
                    setSelectedEvent(null);
                    setIsEditing(false);
                    setEventModalOpen(true);
                  }}
                  style={{
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    background: 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: '0 2px 8px rgba(249, 85, 13, 0.2)',
                    fontFamily: 'Manrope, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(249, 85, 13, 0.2)';
                  }}
                >
                  <Plus size={16} aria-hidden="true" />
                  {isMobile ? 'Add' : 'Add Event'}
                </button>
              )}

              <button
                onClick={onClose}
                aria-label="Close calendar"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e9ecef';
                  e.currentTarget.style.color = '#151133';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.color = '#6c757d';
                }}
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Calendar Container */}
          <div style={{
            flex: 1,
            padding: isMobile ? '12px' : '24px 32px',
            overflow: 'auto',
            background: '#f8f9fa'
          }}>
            {loading ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: isMobile ? '300px' : '500px',
                color: '#6c757d'
              }}>
                Loading calendar...
              </div>
            ) : (
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                padding: isMobile ? '8px' : '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <BigCalendar
                  localizer={localizer}
                  events={calendarEvents}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: isMobile ? 400 : 600 }}
                  defaultView={isMobile ? 'agenda' : 'month'}
                  views={isMobile ? ['agenda', 'month'] : ['month', 'week', 'day', 'agenda']}
                  onSelectEvent={(event: BigCalendarEvent) => {
                    setSelectedEvent(event.resource);
                    setIsEditing(false);
                    setEventModalOpen(true);
                  }}
                  eventPropGetter={(event: BigCalendarEvent) => ({
                    style: {
                      backgroundColor: event.resource.color,
                      borderRadius: '4px',
                      border: 'none',
                      color: '#fff',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: '600',
                      padding: '2px 6px'
                    }
                  })}
                />
              </div>
            )}
          </div>
      </Modal>

      {/* Event Modal */}
      <EventModal
        isOpen={eventModalOpen}
        onClose={() => {
          setEventModalOpen(false);
          setSelectedEvent(null);
          setIsEditing(false);
        }}
        event={selectedEvent}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        canEdit={canEdit}
      />
    </>
  );
};

export default CalendarModal;
